import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { memo, useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Autocomplete } from "@mui/material";
import { useFormikContext, useField } from "formik";
import Fuse from "fuse.js";
import { useDeepCompareMemo } from "use-deep-compare";
import { getHasError } from "../../utils/helpers/getHasError.js";
import { useFormHelpers } from "../../utils/hooks/useFormHelpers.js";
import { useSubmitCount } from "../../utils/hooks/useSubmitCount.js";
import { TextField } from "../../utils/shared/TextField.js";
import { TextFieldLabel } from "../../utils/shared/TextFieldLabel.js";
import { combinedFields } from "../../utils/yup/main.js";
import { FieldHelperText } from "../field-helper-text/field-helper-text.js";
import { Translate } from "../translate/translate.js";
import { insuranceFieldHelpers } from "./insurance-field-helpers.js";
import { Container } from "./Insurance-field.styles.js";
import "yup";
import "../../utils/fields/personalInformation.js";
import "../../../../../type/Appointment.js";
import "../masked-date-input/masked-date-input.js";
import "react-i18next";
import "react-text-mask";
import "../../utils/entities/Form.js";
import "../../../ana-toolbox/ana-toolbox.js";
import "../../../../../constants/date/date.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "date-fns";
import "../../utils/helpers/toBoolean.js";
import "../../utils/helpers/createFields.js";
import "../../utils/helpers/resetFields.js";
import "../../utils/yup/yup.js";
import "../../../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../../utils/helpers/predicates.js";
import "../../utils/yup/emailValidation.js";
import "../../utils/fields/contactDetails.js";
import "../../utils/helpers/featureFlags.js";
import "../../utils/helpers/getAgeFromBirthday.js";
import "../../utils/yup/buildValidationSchema.js";
import "../../utils/fields/miscellaneous.js";
import "../../utils/fields/insurance.js";
import "../../utils/fields/allergiesAndMedications.js";
import "../../../../../type/Disease.js";
import "../../utils/config/medicaments.js";
import "../../utils/fields/wishesAndExpectations.js";
import "../../utils/fields/consents.js";
import "../../utils/fields/profileInterest.js";
const InsuranceFieldBase = memo(
  ({
    companiesOptions: options = [],
    label,
    value: externalValue,
    onChange,
    name = "insurance",
    formik,
    helperText,
    inputValue,
    isRequired,
    error
  }) => {
    const initialInputValue = useRef(externalValue);
    const [isOpen, setIsOpen] = useState(false);
    const fuse = useMemo(() => {
      return new Fuse(options, {
        keys: ["insurerName"],
        threshold: 0.5,
        ignoreLocation: true,
        includeScore: true
      });
    }, [options]);
    const handleChange = (_, option) => {
      if (typeof option !== "string") {
        onChange == null ? void 0 : onChange({
          referenceId: option.referenceId,
          value: option.insurerName
        });
      }
    };
    const handleBlur = (event) => {
      const iv = event.target.value;
      const optionForInputValue = options.find((option) => option.insurerName.toLowerCase() === iv.toLowerCase());
      if (optionForInputValue) {
        onChange == null ? void 0 : onChange({
          referenceId: optionForInputValue == null ? void 0 : optionForInputValue.referenceId,
          value: optionForInputValue == null ? void 0 : optionForInputValue.insurerName
        });
      }
    };
    const handleInputChange = (_, value, reason) => {
      if (reason === "input") {
        onChange == null ? void 0 : onChange({
          referenceId: void 0,
          value
        });
      }
    };
    const handleFocus = (e) => {
      var _a;
      (_a = formik == null ? void 0 : formik.handleFocus) == null ? void 0 : _a.call(formik, e);
      setIsOpen(true);
    };
    const handleClose = () => {
      setIsOpen(false);
    };
    const renderInput = (params) => {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          TextField,
          {
            ...params,
            name,
            error: Boolean(error),
            type: "search",
            label: /* @__PURE__ */ jsx(TextFieldLabel, { required: typeof isRequired === "function" ? false : isRequired, children: /* @__PURE__ */ jsx(Translate, { children: label }) }),
            InputLabelProps: {
              id: `${name}-label`
            },
            InputProps: {
              ...params.InputProps,
              className: void 0,
              style: {
                padding: "22px 16px 3px"
              }
            },
            inputProps: {
              ...params.inputProps,
              value: (externalValue ? selectedOption == null ? void 0 : selectedOption.insurerName : inputValue) || "",
              id: `${name}-input`,
              "aria-labelledby": `${name}-label`,
              autoComplete: "off",
              style: {
                padding: 0
              }
            },
            fullWidth: true
          }
        ),
        /* @__PURE__ */ jsx(FieldHelperText, { name, helperText, error })
      ] });
    };
    const getOptionLabel = (option) => {
      return typeof option === "string" ? option : option.insurerName;
    };
    const isSelected = (option) => option.referenceId === externalValue;
    const selectedOption = options.find(isSelected);
    const hasInitialValue = typeof initialInputValue.current === "string";
    const filterOptions = (o, { inputValue: value }) => {
      if (!value)
        return o;
      const results = insuranceFieldHelpers.getSuggestions(value, {
        fuse,
        getOptionKey: (suggestion) => suggestion.referenceId
      });
      return results;
    };
    const sortedOptions = useMemo(() => {
      return [...options].sort(
        (a, b) => {
          var _a, _b;
          return (_b = getOptionLabel(a)) == null ? void 0 : _b.toLowerCase().localeCompare((_a = getOptionLabel(b)) == null ? void 0 : _a.toLowerCase());
        }
      );
    }, [options]);
    return /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsx(
      Autocomplete,
      {
        open: isOpen,
        options: sortedOptions,
        value: (hasInitialValue ? externalValue : void 0) || "",
        inputValue: (externalValue ? selectedOption == null ? void 0 : selectedOption.insurerName : inputValue) || "",
        getOptionLabel,
        renderInput,
        filterOptions,
        onChange: handleChange,
        onBlur: handleBlur,
        onClose: handleClose,
        onFocus: handleFocus,
        onInputChange: handleInputChange,
        autoHighlight: true,
        disableClearable: true,
        disablePortal: true,
        freeSolo: true
      }
    ) });
  }
);
const InsuranceField = (props) => {
  const submitCount = useSubmitCount();
  const formHelpers = useFormHelpers();
  const { validateForm } = useFormikContext();
  const [field, meta, helpers] = useField(props.name);
  const [idField, idMeta, idHelpers] = useField(`${props.name}Id`);
  const handleChange = useCallback(
    ({ referenceId, value }) => {
      var _a, _b, _c;
      (_b = (_a = fieldSettings == null ? void 0 : fieldSettings.hooks) == null ? void 0 : _a.beforeChange) == null ? void 0 : _b.call(_a, referenceId || value, formHelpers);
      idHelpers.setValue(referenceId);
      helpers.setValue(value);
      (_c = props.onChange) == null ? void 0 : _c.call(props, { value, referenceId });
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    var _a, _b;
    if (idMeta.value) {
      handleChange({
        referenceId: idMeta.value,
        value: ((_b = (_a = props.companiesOptions) == null ? void 0 : _a.find((o) => o.referenceId === idMeta.value)) == null ? void 0 : _b.insurerName) || ""
      });
      validateForm();
    }
  }, []);
  const hasError = getHasError(meta, submitCount ?? 1);
  const memoizedField = useDeepCompareMemo(() => field, [field]);
  const fieldSettings = useMemo(
    () => combinedFields[props.name],
    [props.name]
  );
  return /* @__PURE__ */ jsx(
    InsuranceFieldBase,
    {
      error: hasError ? meta.error : void 0,
      ...props,
      ...memoizedField,
      value: idField.value,
      inputValue: field.value || "",
      onChange: handleChange
    }
  );
};
export {
  InsuranceField
};
